import { Component, OnInit, Input, Output, EventEmitter, ViewContainerRef, forwardRef } from '@angular/core';

import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from 'environments/environment';
import {
  ValidationUtilService,
  ApiRequestService,
  CommonUtilty,
  InteractService,
  AppMetadataHandler,
  ComponentOutputDO
} from 'app'
import { FormBuilder, FormControl, FormGroup, FormArray, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MetadataService } from 'app/BaseFramework/core/http/metadata.service';
import { TechnicalTranslatePipe } from 'app/BaseFramework/shared/custom-pipes';
import { CustomHookService } from 'app/ViewModules/shared-services/custom-hook/custom-hook.service';

/**
 * Input Grid Nested Component
 * 
 * A dynamic nested grid component that supports parent-child relationships in form data.
 * Handles complex form arrays with nested child grids, validation, and custom field rules.
 * Implements ControlValueAccessor for seamless integration with Angular reactive forms.
 * 
 * Features:
 * - Dynamic parent and child grid generation
 * - Custom field rules and hooks
 * - Lookup data management
 * - Form validation and error handling
 * - Data transformation between UI and server formats
 */
@Component({
  selector: 'app-input-grid-nested',
  templateUrl: './input-grid-nested.component.html',
  styleUrls: ['./input-grid-nested.component.scss'],
  providers: [TechnicalTranslatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputGridNestedComponent),
      multi: true
    }]
})
export class InputGridNestedComponent implements OnInit, ControlValueAccessor {

  baseComponentName = 'InputGridNestedComponent'
  _currentComponentName = 'InputGridNestedComponent';
  environment = environment
  exclusionForm: FormGroup
  lookupsToBind = []
  selectorOfThisComponent = 'app-input-grid-nested'
  parentEvent$
  exclusionList: Array<any> = [];
  masterConfig: any = null
  parentFormArrayName: any = "gridForm"
  parentGridForm: FormGroup;
  lookupValueListMap = {}
  exampleConfigModal = false;
  modSearchHospital: boolean = false;
  modSearchRemarkCode: boolean = false;
  extraExternalParams = [];
  CRITERIALOOKUP = [];

  currencyOptions = {
    prefix: this.appMetadataHandler.baseCurrencyPREFIX,
    thousands: this.appMetadataHandler.THOUSANDS_SEPARATOR,
    decimal: this.appMetadataHandler.DECIMAL_SEPARATOR
  }

  supportedTargetAttributeTypes = ["pushLookupValueList", "replaceLookupValueList", "value", "lookupCd", "metaDataAttribute"]
  exampleConfig = {
    "gridConfig": {
      "parentGridTitle": "Tariff",
      "fieldKey": "tariffs",
      "fieldMapping": "providerDO-hospital-tariffs",
      "parentFormArray": "tariffFormArray",
      "canAdd": true,
      "parentRowFormMetaData": [
        {
          "fieldName": "Id",
          "fieldKey": "id",
          "fieldType": "INPUT",
          "lookupCd": null
        },
        {
          "fieldName": "GUID",
          "fieldKey": "guid",
          "fieldType": "INPUT",
          "lookupCd": null
        },
        {
          "fieldName": "Type",
          "fieldKey": "typeCd",
          "fieldType": "COMBO",
          "lookupCd": "tariffTypeCd",
          "sortOrder": 2,
          "visibility": true,
          "width": "140px"
        },
        {
          "fieldName": "Provider Name",
          "fieldKey": "name",
          "fieldType": "INPUT",
          "lookupCd": null,
          "sortOrder": 0,
          "visibility": true,
          "width": "140px"
        },
        {
          "fieldName": "Status",
          "fieldKey": "status",
          "fieldType": "COMBO",
          "lookupCd": "tariffStatusCd",
          "sortOrder": 1,
          "visibility": true,
          "width": "140px"
        },
        {
          "fieldName": "Valid From",
          "fieldKey": "validFrom",
          "fieldType": "DATE",
          "ignoreDateTimeConversion": true,
          "lookupCd": null,
          "sortOrder": 2,
          "visibility": true,
          "width": "100px"
        },
        {
          "fieldName": "Valid to",
          "fieldKey": "validTo",
          "fieldType": "DATE",
          "ignoreDateTimeConversion": true,
          "lookupCd": null,
          "sortOrder": 2,
          "visibility": true,
          "width": "100px"
        },
        {
          "fieldName": "Provider ID",
          "fieldKey": "providerId",
          "fieldType": "CUSTOM",
          "customTemplateName": "providerIDSearchAndLink",
          "lookupCd": null,
          "sortOrder": 3,
          "visibility": false,
          "width": "140px"
        },
        {
          "fieldName": "Tariff Details",
          "fieldKey": "tariffDetails",
          "fieldType": "FORMARRAY",
          "isChildGrid": true,
          "childGridTitle": "Tariff Details",
          "canAdd": true,
          "childGridMetaData": [
            {
              "fieldName": "Id",
              "fieldKey": "id",
              "fieldType": "INPUT",
              "lookupCd": null,
              "visibility": false,
              "width": "140px"
            },
            {
              "fieldName": "GUID",
              "fieldKey": "guid",
              "fieldType": "INPUT",
              "lookupCd": null,
              "visibility": false,
              "width": "140px"
            },
            {
              "fieldName": "Item",
              "fieldKey": "itemCd",
              "fieldType": "COMBO",
              "lookupCd": "healthClaimBillItemsCd",
              "comboMode": 'multiple',
              "comboBinding": 'CSV',
              "sortOrder": 0,
              "visibility": true,
              "width": "140px",
              "fieldRules": [
                {
                  "methodName": "getSubItemList",
                  "methodParameters": [
                    "itemCd"
                  ],
                  "extractAttributeMapping": "billItemDOList",
                  "targetFieldKey": "subItemCd",
                  "targetAttributeType": "replaceLookupValueList",
                  "lookupKeyVariable": "billSubItemCd",
                  "lookupValueVariable": "billSubItemDesc"
                }
              ]
            },
            {
              "fieldName": "Sub Item",
              "fieldKey": "subItemCd",
              "fieldType": "COMBO",
              "lookupCd": "healthClaimBillSubItemsCd",
              "sortOrder": 1,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Rate Type",
              "fieldKey": "rateTypeCd",
              "fieldType": "COMBO",
              "lookupCd": "billItemDisbursementTypeCd",
              "lookupValues": [],
              "sortOrder": 2,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Room Type",
              "fieldKey": "roomTypeCd",
              "fieldType": "COMBO",
              "lookupCd": "RoomTypeCd",
              "sortOrder": 3,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Quantity",
              "fieldKey": "quantity",
              "fieldType": "INPUT",
              "lookupCd": null,
              "sortOrder": 4,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Rate",
              "fieldKey": "rate",
              "fieldType": "CURRENCYINPUT",
              "lookupCd": null,
              "sortOrder": 5,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Remarks",
              "fieldKey": "remarks",
              "fieldType": "TEXTAREA",
              "lookupCd": null,
              "maxlength": 500,
              "sortOrder": 6,
              "visibility": true,
              "width": "140px"
            },
            {
              "fieldName": "Is Excluded?",
              "fieldKey": "exclusionFl",
              "fieldType": "CHECKBOX",
              "lookupCd": null,
              "sortOrder": 7,
              "visibility": true,
              "width": "140px"
            }
          ],
          "lookupCd": null,
          "sortOrder": 0
        }
      ]
    },
    "isDisabled": null
  }

  // ===========================
  // INPUT/OUTPUT PROPERTIES
  // ===========================

  @Output() componentOutput = new EventEmitter<any>();
  @Input() type: string;
  @Input() transactionState: string;
  @Input() maxDepth;
  @Input() FORMNAME = "parentGridForm"
  @Input() CHILDGRIDFORM = "childGridForm"

  private _extractElementFromArray
  private _inputDataForEdit = null;
  private _initialPayload

  @Input()
  set currentComponent(value) {
    if (value) this._currentComponentName = value
  }

  get currentComponent() {
    return this._currentComponentName
  }

  @Input()
  set ExtractElementFromArray(value) {
    this._extractElementFromArray = value
  }

  get ExtractElementFromArray() {
    return this._extractElementFromArray
  }

  get flatteningSafetyValue() {
    if (this._extractElementFromArray) {
      return 'unsafe'
    }
    else {
      return 'safe'
    }
  }

  @Input()
  set componentMetaData(value) {
    if (value) {
      //gridConfig
      let extractedConfig = value.gridConfig ? this.commonUtilty.deepCopy(value.gridConfig) : this.commonUtilty.deepCopy(value)
      //parentGridTitle
      extractedConfig = this.attributeMapper(extractedConfig, 'parentGridTitle', 'heading');
      //parentRowFormMetaData
      extractedConfig = this.attributeMapper(extractedConfig, 'parentRowFormMetaData', 'columns');
      //canAdd
      extractedConfig = this.attributeMapper(extractedConfig, 'canAdd', 'enableAddRemoveRows');
      //parentFormArray
      extractedConfig['parentFormArray'] = this.parentFormArrayName
      //Final Config assigned
      this.masterConfig = this.commonUtilty.deepCopy(extractedConfig)
    }
    else {
      console.warn(`Metadata Missing for ${this.getCurrentComponentName()}`)
    }
  }

  get componentMetaData() {
    return this.masterConfig
  }

  @Input()
  set IO(value: any) {
    if (value) {
      this._inputDataForEdit = this.ServerToUITransform(value, this.componentMetaData);
      this._initialPayload = this.commonUtilty.deepCopy(value)
    }
  };

  @Input()
  set inputDataForEdit(value: any) {
    if (value) {
      this._inputDataForEdit = value;
    }
  };

  get inputDataForEdit() {
    return this._inputDataForEdit
  }

  // ===========================
  // INITIALIZATIONS
  // ===========================

  lookupSubjects = this.commonUtilty.initializeLookupList(this.lookupsToBind)
  lookup$ = {}

  // ===========================
  // CONSTRUCTOR
  // ===========================

  constructor(
    private viewContainerRef: ViewContainerRef,
    public validationUtilService: ValidationUtilService,
    public apiRequestService: ApiRequestService,
    private fb: FormBuilder,
    private commonUtilty: CommonUtilty,
    private interactService: InteractService,
    public appMetadataHandler: AppMetadataHandler,
    public metadataService: MetadataService,
    public msg: NzMessageService,
    private customHookService: CustomHookService
  ) { }

  // ===========================
  // CONTROL VALUE ACCESSOR IMPLEMENTATION
  // ===========================

  /**
   * Implements ControlValueAccessor writeValue method
   */
  writeValue(obj: any): void {
    if (obj !== undefined) {
      // MainList = obj;
    }
  }

  propagateChange = (_: any) => { };

  /**
   * Registers onChange callback for ControlValueAccessor
   */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers onTouched callback for ControlValueAccessor
   */
  registerOnTouched(fn: any): void {
  }

  /**
   * Sets disabled state for ControlValueAccessor
   */
  setDisabledState?(isDisabled: boolean): void { }

  // ===========================
  // LIFECYCLE METHODS
  // ===========================

  /**
   * Component initialization lifecycle hook
   */
  ngOnInit() {
    const consoleStyle = 'color: red; padding: 2px 5px; border-radius: 2px'
    if (environment && !environment.production) {
      console.group(`%c ${this.baseComponentName}-${this._currentComponentName}: Usage detected on ' + ${this.getParentComponentName()}`, `color: white; background-color: orange; padding: 2px 5px; border-radius: 2px`);
      console.log(`%c Current TargetAttributeType Support for: ${this.supportedTargetAttributeTypes.join(', ')} `, consoleStyle)
      console.log(`%cExample , <${this.getCurrentComponentName()} (componentOutput)='doSomethingOnDataReceived()'></${this.getCurrentComponentName()}>`, consoleStyle)
      console.groupEnd();
    }
    this.lookupsToBind = this.extractLookupToBindFromConfig(this.componentMetaData, this.lookupsToBind)
    this.lookup$ = this.commonUtilty.initializeLookupList(this.lookupsToBind);

    // Data Specific Method Calls
    this.metaDataManipulations();
    this.initForm(this._inputDataForEdit);
    this.detectChangeAndPropogateData(this.parentGridForm)
    this.subscribeToParentEvent()
  }

  // ===========================
  // COMPONENT UTILITY METHODS
  // ===========================

  /**
   * Emits component output with proper transformation
   */
  EmitComponentOutput(data) {
    new ComponentOutputDO(this.getCurrentComponentName(), data, this.componentOutput, this.UItoServerTransform)
  }

  /**
   * Gets the parent component instance
   */
  getParentComponent(): Component {
    try {
      return this.viewContainerRef['_data'].componentView.component.viewContainerRef['_view'].component
    } catch (error) {
    }
  }

  /**
   * Gets the parent component name
   */
  getParentComponentName(): string {
    try {
      return this.viewContainerRef['_data'].componentView.component.viewContainerRef['_view'].component.constructor.name
    } catch (error) {
    }
  }

  /**
   * Gets the current component name
   */
  getCurrentComponentName(): string {
    return this._currentComponentName
  }

  /**
   * Maps attributes from source to target
   */
  private attributeMapper(extractedConfig: any, sourceAttr, targetAttr) {
    if (extractedConfig[sourceAttr])
      extractedConfig[targetAttr] = extractedConfig[sourceAttr];
    else if (extractedConfig[targetAttr])
      extractedConfig[sourceAttr] = extractedConfig[targetAttr];
    return extractedConfig
  }

  /**
   * Performs spell check on phrases
   */
  spellCheck(phrase) {
    return this.commonUtilty.spellCheck(phrase)
  }

  // ===========================
  // METADATA MANIPULATION METHODS
  // ===========================

  /**
   * Performs metadata manipulations and sorting
   */
  private metaDataManipulations() {
    this.masterConfig.parentRowFormMetaData = this.commonUtilty.sortDateObject('sortOrder', this.masterConfig.parentRowFormMetaData, 'asc');
    let filteredParentRows = this.masterConfig.parentRowFormMetaData.filter(parentRow => parentRow.fieldType == 'FORMARRAY');
    if (filteredParentRows.length < 2) {
      this.masterConfig['parentRowFormMetaDataMap'] = this.commonUtilty.convertArrayToMap(filteredParentRows, 'fieldType') || {};
      if (this.masterConfig['parentRowFormMetaDataMap'] && this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'] && this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData']) {
        this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'] = this.commonUtilty.sortDateObject('sortOrder', this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'], 'asc');
      }
    }
    else {
      console.error('Two FORMARRAYs found', filteredParentRows);
      throw `In ${this.getCurrentComponentName()}, the parentRowFormMetaData cannot have more than 1 fieldType as FORMARRAY`;
    }
  }

  /**
   * Extracts lookup codes to bind from configuration
   */
  extractLookupToBindFromConfig(config, lookupsToBind) {
    if (this.commonUtilty.isArrayIterable(config.parentRowFormMetaData) && Array.isArray(lookupsToBind)) {
      config.parentRowFormMetaData.forEach(parentRow => {
        lookupsToBind = this.commonUtilty.uniquePushInArray(lookupsToBind, parentRow.lookupCd)
        if (parentRow.isChildGrid && this.commonUtilty.isArrayIterable(parentRow.childGridMetaData)) {
          parentRow.childGridMetaData.forEach(childRow => {
            lookupsToBind = this.commonUtilty.uniquePushInArray(lookupsToBind, childRow.lookupCd)
          });
        }
      });
    }
    return lookupsToBind
  }

  // ===========================
  // FORM MANIPULATION METHODS
  // ===========================

  /**
   * Initializes the main form structure
   */
  initForm(data?) {
    let output = data || []
    this.parentGridForm = this.fb.group({
      [this.masterConfig.parentFormArray]: this.fb.array([])
    });
    this.initilizeFormWithData(output)
  }

  /**
   * Initializes form with provided data
   */
  initilizeFormWithData(data) {
    let output = data && (Array.isArray(data) && data.length == 0) ? [{}] : data
    if (this.commonUtilty.isArrayIterable(output)) {
      output.forEach(bill => {
        this.addRow(bill);
      });
    }
  }

  /**
   * Initializes parent row form with metadata and data
   */
  initParentRowForm(metaData, FormName, data?, parentIndex?): FormGroup {
    let payload = data || {}
    if (this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'] && !payload[this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey]) {
      payload[this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey] = [{}]
    }
    let formObject = this.formFactory(metaData, FormName, payload, parentIndex)
    if (parentIndex) {
      let indexControl = this.validationUtilService.setValidationsForFormControl('index', this.FORMNAME, parentIndex)
      formObject.addControl('index', indexControl)
    }
    return formObject
  }

  /**
   * Adds a new row to the parent grid
   */
  addRow(data?) {
    let dataForEdit = data || {}
    const control = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
    control.push(this.initParentRowForm(this.masterConfig.parentRowFormMetaData, this.FORMNAME, dataForEdit, control.value.length));
  }

  /**
   * Adds a new row to the child grid
   */
  addChildRow(parentIndex, data?) {
    let dataForEdit = data || {}
    const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
    const parentControl = <FormControl>parentControlList.controls[parentIndex]
    const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
    childControlList.push(this.initParentRowForm(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'], this.FORMNAME, dataForEdit, childControlList.value.length));
  }

  /**
   * Removes a row from the parent grid
   */
  removeRow(index: number) {
    const control = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
    let removeItem = this.parentGridForm.value[this.masterConfig.parentFormArray][index]

    if (this.commonUtilty.isArrayIterable(this._inputDataForEdit) && removeItem) {
      this._inputDataForEdit.forEach((e, i) => {
        if (e.id == removeItem.id) {
          this._inputDataForEdit.splice(i, 0)
        }
      });
    }

    control.removeAt(index);
  }

  /**
   * Removes a row from the child grid
   */
  removeChildRow(parentIndex, index: number) {
    const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
    const parentControl = <FormControl>parentControlList.controls[parentIndex]
    const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
    let removeItem = <FormControl>childControlList.value[index]

    if (this.commonUtilty.isArrayIterable(this._inputDataForEdit) && removeItem.value) {
      this._inputDataForEdit.forEach((e, i) => {
        if (e.id == parentControl.value.id) {
          if (this.commonUtilty.isArrayIterable(e[this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey])) {
            e[this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey].forEach((billBreakupItem, i2) => {
              if (billBreakupItem.id == removeItem.value.id)
                this._inputDataForEdit[i][this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey].splice(i2, 0)
            });
          }
        }
      });
    }

    childControlList.removeAt(index);
  }

  /**
   * Creates form controls based on metadata configuration
   */
  formFactory(metaData, FormName, dataObject = {}, parentIndex?, childIndex?) {
    let formDataOuput = {}
    if (metaData && Array.isArray(metaData)) {
      metaData.forEach(field => {
        let intialValue
        if (field.fieldType == 'DATE') {
          intialValue = this.commonUtilty.serverDOtoUIDODate(dataObject[field.fieldKey]) || this.commonUtilty.serverDOtoUIDODate(field.defaultValue)
        }
        else if (field.fieldType == 'TIME') {
          intialValue = this.commonUtilty.serverDOtoUIDOsetTime(dataObject[field.fieldKey]) || this.commonUtilty.serverDOtoUIDOsetTime(field.defaultValue)
        }
        else if (field.fieldType == 'COMBO' && field.comboMode == 'multiple') {
          intialValue = this.commonUtilty.convertCSVToArray(dataObject[field.fieldKey]) || this.commonUtilty.convertCSVToArray(field.defaultValue)
        }
        else if (field.fieldType == 'FORMARRAY') {
          let outputFormList = []
          let childData = dataObject[field.fieldKey]
          if (this.commonUtilty.isArrayIterable(childData)) {
            childData.forEach((childRowData, rowChildIndex) => {
              let form = this.formFactory(field.childGridMetaData, field.childFormName, childRowData, parentIndex, rowChildIndex)
              let indexControl = this.validationUtilService.setValidationsForFormControl('index', field.childFormName, outputFormList.length)
              if (form) {
                form.addControl('index', indexControl)
                outputFormList.push(form)
              }
              else {
                console.error("Child Form got invalid data - ", field)
              }
            });
          }
          intialValue = this.fb.array(outputFormList)
        }
        else {
          intialValue = dataObject[field.fieldKey] || field.defaultValue
        }

        if (field.fieldType == 'FORMARRAY') {
          formDataOuput[field.fieldKey] = intialValue
        }
        else {
          formDataOuput[field.fieldKey] = this.validationUtilService.setValidationsForFormControl(
            field.fieldKey, FormName, intialValue)
        }

        let isInitCall = true
        this.fieldDataChangeHandler(field, field.fieldKey, dataObject, parentIndex, childIndex, isInitCall)
      })
    }

    let output = this.fb.group(formDataOuput)
    return output
  }

  // ===========================
  // FIELD VALUE MANIPULATION METHODS
  // ===========================

  /**
   * Sets value on a child row field
   */
  setValueOnChildRow(parentIndex, childIndex: number, fieldKey, fieldValue) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
      let controlToUpdate = <FormControl>childControlList.controls[childIndex]['controls'][fieldKey]

      controlToUpdate.setValue(fieldValue)
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Gets value from a child row field
   */
  getValueOnChildRow(parentIndex, childIndex: number, fieldKey) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
      let controlToUpdate = <FormControl>childControlList.controls[childIndex]['controls'][fieldKey]
      return controlToUpdate.value[fieldKey]
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Sets value on a parent row field
   */
  setValueOnParentRow(parentIndex, fieldKey, fieldValue) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      parentControl.setValue(fieldValue)
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Gets value from a parent row field
   */
  getValueOnParentRow(parentIndex, fieldKey) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      return parentControl.value[fieldKey]
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Resets value on a parent row field
   */
  resetValueOnParentRow(parentIndex, fieldKey) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]['controls'][fieldKey]
      return parentControl.reset()
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Resets value on a child row field
   */
  resetValueOnChildRow(parentIndex, childIndex: number, fieldKey) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
      let controlToUpdate = <FormControl>childControlList.controls[childIndex]['controls'][fieldKey]

      controlToUpdate.reset()
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  /**
   * Resets value on all child row fields
   */
  resetValueOnAllChildRow(parentIndex, fieldKey) {
    try {
      const parentControlList = <FormArray>this.parentGridForm.get(this.masterConfig.parentFormArray);
      const parentControl = <FormControl>parentControlList.controls[parentIndex]
      const childControlList = <FormArray>parentControl.get(this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey)
      let extractedFieldKey = fieldKey.substring(7);  //$child-serviceCd
      childControlList.controls.forEach(control => {
        let controlToUpdate = control['controls'][extractedFieldKey]
        controlToUpdate.reset()
      })
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  // ===========================
  // FIELD RULES AND CUSTOM HOOKS
  // ===========================

  /**
   * Handles field data changes and executes custom hooks
   */
  fieldDataChangeHandler(fieldMetaData, fieldKey, formData, parentIndex, childIndex?, isInitCall?) {
    let customHooks = fieldMetaData ? fieldMetaData.customHooks : null
    if (this.commonUtilty.isArrayIterable(customHooks)) {
      customHooks.forEach(rule => {
        let serviceCall = this.customHookService[rule.methodName].bind(this)
        let parameterValues = []
        if (this.commonUtilty.isArrayIterable(rule.methodParameters) && formData)
          rule.methodParameters.forEach(parameter => {
            if (parameter.includes('$parent-')) {
              let actualParameterKey = parameter.split('-').slice(-1).pop()
              parameterValues.push(this.getValueOnParentRow(parentIndex, actualParameterKey))
            }
            else
              parameterValues.push(formData[parameter])
          });
        else {
          console.warn(`Either methodParameters or form is empty`, customHooks, formData)
        }
        serviceCall(...parameterValues).subscribe(resp => {
          if (resp) {
            let flattendResp = this.commonUtilty.plainflattenObject(resp)
            let valueToExtract
            if (rule.extractAttributeMapping)
              valueToExtract = flattendResp[rule.extractAttributeMapping]
            else
              valueToExtract = this.commonUtilty.deepCopy(resp)
            if (Array.isArray(valueToExtract)) {
              valueToExtract.forEach((v, i) => {
                valueToExtract[i]['lookupKey'] = v[rule.lookupKeyVariable]
                valueToExtract[i]['lookupValue'] = v[rule.lookupValueVariable]
              })
            }
            this.targetActionEvaluator(rule, parentIndex, childIndex, valueToExtract, isInitCall);
          }
        });
      });
    } else {
      if (!environment.production && fieldMetaData && !fieldMetaData.customHooks && fieldMetaData.fieldRules)
        this.msg.error(`Configuration Error: 'fieldRules' attribute is now called 'customHooks' on ${this.baseComponentName}`)
    }
  }

  /**
   * Evaluates target actions based on rules
   */
  private targetActionEvaluator(rule: any, parentIndex: any, childIndex: any, valueToExtract: any, isInitCall: any) {
    switch (rule.targetAttributeType) {
      case "replaceLookupValueList":
        this.lookupValueListMap[`${rule.targetFieldKey}-${parentIndex}${childIndex != 0 && !childIndex ? '' : childIndex}`] = this.commonUtilty.deepCopy(valueToExtract);
        this.resetParentOrChildField(isInitCall, parentIndex, childIndex, rule.targetFieldKey);
        break;
      case "pushLookupValueList":
        this.lookupValueListMap[`${rule.targetFieldKey}-${parentIndex}${childIndex != 0 && !childIndex ? '' : childIndex}`] = this.commonUtilty.uniquePushInArray(this.lookupValueListMap[`${rule.targetFieldKey}-${parentIndex}${childIndex}`], 'lookupKey');
        break;
      case "value":
        if (parentIndex && childIndex) {
          this.setValueOnChildRow(parentIndex, childIndex, rule.targetFieldKey, valueToExtract);
        }
        else {
          this.setValueOnParentRow(parentIndex, rule.targetFieldKey, valueToExtract);
        }
        break;
      case "metaDataAttribute": {
        //For Child Row
        if (typeof parentIndex === 'number' && typeof childIndex === 'number') {
          //To Set Multiple attributes on config, this can be used.
          if (this.commonUtilty.testJSON(JSON.stringify(valueToExtract)))
            this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'][childIndex] = Object.assign({}, this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'][childIndex], valueToExtract)
          //To set one attribute on config 
          else
            this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']['childGridMetaData'][childIndex][rule.targetAttribute] = valueToExtract;
        }
        //For Parent Row
        else {
          //To Set Multiple attributes on config, this can be used.
          if (this.commonUtilty.testJSON(valueToExtract))
            this.masterConfig.parentRowFormMetaData[parentIndex] = Object.assign({}, this.masterConfig.parentRowFormMetaData[parentIndex], valueToExtract)
          //To set one attribute on config 
          else
            this.masterConfig.parentRowFormMetaData[parentIndex][rule.targetAttribute] = valueToExtract;
        }
      }
        break;
      default:
        if (!this.environment.production)
          this.msg.error(`Configuration Error: Unsupported targetAttributeType - ${rule.targetAttributeType} on ${this._currentComponentName} config`)
        break;
    }
  }

  /**
   * Resets parent or child field based on conditions
   */
  private resetParentOrChildField(isInitCall: any, parentIndex: any, childIndex: any, targetFieldKey: any) {
    try {
      if (!isInitCall) {
        if (typeof parentIndex === 'number' && typeof childIndex === 'number') {
          this.resetValueOnChildRow(parentIndex, childIndex, targetFieldKey);
        }
        else if (typeof parentIndex === 'number' && !(typeof childIndex === 'number')) {
          this.resetValueOnAllChildRow(parentIndex, targetFieldKey);
        }
        else {
          this.resetValueOnParentRow(parentIndex, targetFieldKey);
        }
      }
    }
    catch {
      console.warn(`Minor issue on form Detected`)
    }
  }

  // ===========================
  // DATA TRANSFORMATION METHODS
  // ===========================

  /**
   * Transforms UI data to server format
   */
  UItoServerTransform(payload, response) {
    let DO = this.commonUtilty.getDOName(payload)
    let flattenedIO = this.commonUtilty.plainflattenObject(payload, this.flatteningSafetyValue, this.maxDepth)
    let metaData = response.componentData.metaData
    if (payload && payload[DO] && flattenedIO && metaData.fieldMapping) {
      flattenedIO[metaData.fieldMapping] = this.commonUtilty.deepCopy(response.componentData.data)
    }

    /**
     * Datatype wise manipulations
     */
    let fieldKeyWiseParentRowMetaData = this.commonUtilty.convertArrayToMap(metaData.parentRowFormMetaData, 'fieldKey')
    let dataTypeWiseParentRowMetaData = this.commonUtilty.convertArrayToMap(metaData.parentRowFormMetaData, 'fieldType')
    let fieldKeyWiseChildRowMetaData = dataTypeWiseParentRowMetaData['FORMARRAY'] && this.commonUtilty.isArrayIterable(dataTypeWiseParentRowMetaData['FORMARRAY']['childGridMetaData']) ? this.commonUtilty.convertArrayToMap(dataTypeWiseParentRowMetaData['FORMARRAY']['childGridMetaData'], 'fieldKey') : []
    if (this.commonUtilty.isArrayIterable(flattenedIO[metaData.fieldMapping])) {
      flattenedIO[metaData.fieldMapping].forEach((parentRow, parentIndex) => {
        for (const parentRowFieldKey in parentRow) {
          if (fieldKeyWiseParentRowMetaData[parentRowFieldKey]) {
            if (fieldKeyWiseParentRowMetaData[parentRowFieldKey].fieldType == 'DATE' && !fieldKeyWiseParentRowMetaData[parentRowFieldKey].ignoreDateTimeConversion) {
              flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey] = this.commonUtilty.UIDOtoServerDODate(parentRow[parentRowFieldKey])
            }
            else if (fieldKeyWiseParentRowMetaData[parentRowFieldKey].fieldType == 'TIME' && !fieldKeyWiseParentRowMetaData[parentRowFieldKey].ignoreDateTimeConversion) {
              flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey] = this.commonUtilty.UIDOtoServerDOsetTime(parentRow[parentRowFieldKey])
            }
            else if (fieldKeyWiseParentRowMetaData[parentRowFieldKey].fieldType == 'COMBO') {
              if (fieldKeyWiseParentRowMetaData[parentRowFieldKey] && fieldKeyWiseParentRowMetaData[parentRowFieldKey].comboMode == 'multiple') {
                if (fieldKeyWiseParentRowMetaData[parentRowFieldKey].comboBindingType == 'CSV')
                  flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey] = this.commonUtilty.convertArrayToCSV(parentRow[parentRowFieldKey])
              }
            }
            else if (fieldKeyWiseParentRowMetaData[parentRowFieldKey].fieldType == 'FORMARRAY') {
              if (this.commonUtilty.isArrayIterable(fieldKeyWiseParentRowMetaData[parentRowFieldKey]['childGridMetaData'])) {
                flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey].forEach((childRow, childIndex) => {
                  for (const childRowFieldKey in childRow) {
                    if (fieldKeyWiseChildRowMetaData[childRowFieldKey]) {
                      if (fieldKeyWiseChildRowMetaData[childRowFieldKey].fieldType == 'DATE' && !fieldKeyWiseChildRowMetaData[childRowFieldKey].fieldType.ignoreDateTimeConversion) {
                        flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey][childIndex][childRowFieldKey] = this.commonUtilty.UIDOtoServerDODate(childRow[childRowFieldKey])
                      }
                      else if (fieldKeyWiseChildRowMetaData[childRowFieldKey].fieldType == 'TIME' && !fieldKeyWiseChildRowMetaData[childRowFieldKey].fieldType.ignoreDateTimeConversion) {
                        flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey][childIndex][childRowFieldKey] = this.commonUtilty.UIDOtoServerDOsetTime(childRow[childRowFieldKey])
                      }
                      else if (fieldKeyWiseChildRowMetaData[childRowFieldKey].fieldType == 'COMBO') {
                        if (fieldKeyWiseChildRowMetaData[childRowFieldKey] && fieldKeyWiseChildRowMetaData[childRowFieldKey].comboMode == 'multiple') {
                          if (fieldKeyWiseChildRowMetaData[childRowFieldKey].comboBindingType == 'CSV')
                            flattenedIO[metaData.fieldMapping][parentIndex][parentRowFieldKey][childIndex][childRowFieldKey] = this.commonUtilty.convertArrayToCSV(childRow[childRowFieldKey])
                        }
                      }
                    }
                  }
                });
              }
            }
          }
        }
      })
    }
    /**
     * Datatype wise manipulations Ends
     */
    if (this.appMetadataHandler.featureFlags.enableTransformLogForSteps && environment.production) {
      console.log(`UIToServer Transformation ${response.componentName} : ${JSON.stringify(payload)}`)
    }
    let output = this.commonUtilty.plainUnflattenObject(flattenedIO, this.flatteningSafetyValue, this.maxDepth)
    return output
  }

  /**
   * Transforms server data to UI format
   */
  ServerToUITransform(payload, metaData) {
    let DO = this.commonUtilty.getDOName(payload);
    let flattenedIO = this.commonUtilty.plainflattenObject(payload, this.flatteningSafetyValue, this.maxDepth)
    let output = [];
    if (payload && payload[DO] && flattenedIO) {
      if (flattenedIO[metaData.fieldMapping]) output = this.commonUtilty.plainUnflattenObject(flattenedIO[metaData.fieldMapping], this.flatteningSafetyValue, this.maxDepth);
    }
    return output;
  }

  // ===========================
  // UTILITY AND HELPER METHODS
  // ===========================

  /**
   * Checks and removes empty rows from form data
   */
  checkEmptyRows(Form) {
    let parentDataList = []
    if (Form) {
      parentDataList = Form.value[this.masterConfig.parentFormArray]
      if (Array.isArray(parentDataList)) {
        parentDataList.forEach((parentDataItem, parentIndex) => {
          let isParentDataItemValid = false
          let childFormArray = this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY']
          for (const key in parentDataItem) {
            let isChildItemValid = false
            if (childFormArray && key == childFormArray.fieldKey && parentDataItem[key] && Array.isArray(parentDataItem[key])) {
              parentDataItem[key].forEach((billitem, billItemIndex) => {
                for (const key in billitem) {
                  if (billitem[key]) {
                    isChildItemValid = true
                  }
                }
                if (!isChildItemValid) {
                  parentDataList[parentIndex][this.masterConfig['parentRowFormMetaDataMap']['FORMARRAY'].fieldKey].splice(billItemIndex, 1)
                }
              });
            }
            if (parentDataItem[key]) {
              isParentDataItemValid = true
            }
          }

          if (!isParentDataItemValid) {
            parentDataList.splice(parentIndex, 1)
          }
        });
      }
    }
    return parentDataList
  }

  // ===========================
  // MODAL AND SEARCH METHODS
  // ===========================

  /**
   * Opens search remark code modal
   */
  openSearchRemarkCodeModal() {
    this.modSearchRemarkCode = true
  }

  /**
   * Fetches search results
   */
  fetchSearchResult(event) {
    console.log(event);
  }

  // ===========================
  // EVENT HANDLING AND SUBSCRIPTION
  // ===========================

  /**
   * Subscribes to parent component events
   */
  subscribeToParentEvent() {
    if (this.parentEvent$ != null)
      this.parentEvent$.unsubscribe()
    this.parentEvent$ = this.interactService.pingSubject$.subscribe((param: any) => {
      const matches = param.filter(s => s.includes(this.getCurrentComponentName()));
      if (this.commonUtilty.isArrayIterable(param) && this.commonUtilty.isArrayIterable(matches)) {
        let validationResult = this.validationUtilService.validateOutput(this.parentGridForm, this.masterConfig.parentRowFormMetaData, this.getCurrentComponentName())
        if (!validationResult.isInvalid) {
          let extractData = this.checkEmptyRows(this.parentGridForm)
          let output = { metaData: this.componentMetaData, data: extractData }
          this.EmitComponentOutput(output)
        }
        else {
          this.EmitComponentOutput(validationResult)
        }
      }
      else {
        console.warn(`${this.getCurrentComponentName()} received the ping, but is not a emitter child for current transaction type.`)
      }
    })
  }

  /**
   * Detects form changes and propagates data
   */
  detectChangeAndPropogateData(form) {
    if (form) {
      let firstOutput = this.commonUtilty.deepCopy(form.value)
      this.propagateChange(firstOutput)
      form.valueChanges.subscribe(() => {
        let formData = form.value || []
        if (this.commonUtilty.isArrayIterable(formData[this.masterConfig.parentFormArray])) {
          this.propagateChange(formData)
        } else {
          this.propagateChange(this._inputDataForEdit)
        }
      })
    }
  }

  onlyAlphaNumericAllowedOnPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData?.getData('text') || '';
    if (!/^[a-zA-Z0-9]*$/.test(pastedText)) {
      event.preventDefault();
    }
  }
  
}
